import { LockClosedIcon } from '@heroicons/react/outline';
import { Tooltip } from 'antd';
import { CardIcons, InternationalCardIcons } from 'assets';
import BoltIcon from 'assets/Icons/BoltIcon';
import InfoCircleIcon from 'assets/Icons/InfoCircle';
import OfferIcon from 'assets/Icons/OfferIcon';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import Cookies from 'js-cookie';
import moment from 'moment';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';

import { ShouldRender } from '@/components/shared/basic/ShouldRender';

import { countryAtom, getGlobalCountry } from '@/atoms/countryState';
import { getRewardRulesByName } from '@/services/reward';
import { convertCurrency } from '@/utils/currency-convert';

const PaymentAvailability = () => {
  const [countryState] = useRecoilState(countryAtom);
  const [cashBack, setCashBack] = useState<any>(0);
  const [rate, setRate] = useState<number | null>(null);

  const CASHBACK_COOKIE_KEY = 'cashback';


  const globalCountry = getGlobalCountry();

  const offers = [
    {
      text: (
        <span className=' text-[9px] xs:text-[11px]  uppercase text-white md:text-sm font-medium'>
          Instant Digital Access
        </span>
      ),
      image: <BoltIcon />,
      type: 'icon',
      check: true,
    },
    {
      text: (
        <span className=' text-[9px] xs:text-[11px]  uppercase text-white  md:text-sm font-medium'>
          Get{' '}
          <p className='text-xs md:text-[15px] inline-flex px-0.5'>
            {cashBack} Z coins as
          </p>{' '}
          cashback
        </span>
      ),
      image: <OfferIcon />,
      type: 'icon',
      check: countryState.selectedCountry?.code === 'IN',
    },
  ];

  const fetchRate = async () => {
    const fromCurrency = 'INR';
    const toCurrency = globalCountry?.currency as string;

    if (fromCurrency === toCurrency) {
      setRate(1);
      return;
    }

    const cacheKey = `rate_${fromCurrency}_${toCurrency}`;
    const cached = localStorage.getItem(cacheKey);

    if (cached && cached !== 'undefined' && Number(cached)) {
      setRate(Number(cached));
      return;
    }

    try {
      const result = await convertCurrency(fromCurrency, toCurrency, 1);

      if (result) {
        localStorage.setItem(cacheKey, result.toString());
        setRate(result);
      }
    } catch (error) {
      /* empty */
    }
  };

  const handleCashbackCoin = async (setCoins: (coins: any) => void) => {
    try {
      const cached = Cookies.get(CASHBACK_COOKIE_KEY);

      if (cached) {
        const parsed = JSON.parse(cached);
        const now = moment();
        const expiry = moment(parsed.expiry);

        if (now.isBefore(expiry)) {
          setCoins(parsed.data);
          return;
        }
      }

      const data = await getRewardRulesByName(CASHBACK_COOKIE_KEY);
      setCoins(data.data);

      const expiryTime = moment().add(6, 'hours').toISOString();

      Cookies.set(
        CASHBACK_COOKIE_KEY,
        JSON.stringify({
          data: data.data,
          expiry: expiryTime,
        }),
        { expires: 1 },
      );
    } catch (error: any) {
      /* empty */
    }
  };

  useEffect(() => {
    handleCashbackCoin(setCashBack);
  }, []);

  useEffect(() => {
    fetchRate();
  }, [globalCountry?.currency]);

  return (
    <div className='flex flex-col  md:ml-2.5 w-full '>
      <ShouldRender check={countryState.selectedCountry?.code === 'IN'}>
        <div
          className={clsx(' flex translate-y-[2px] md:translate-y-0 w-full')}
        >
          <div
            className={clsx(' w-fit relative cursor-pointer overflow-hidden')}
          >
            {/* Shimmer Effect */}
            <motion.div
              className='absolute top-0 left-0 z-30 cursor-pointer h-full   w-full '
              style={{
                background:
                  'linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.2) 50%, rgba(255, 255, 255, 0) 100%)',
                transform: 'translateX(-100%)',
              }}
              animate={{ translateX: ['-100%', '100%'] }}
              transition={{
                duration: 2,
                ease: 'linear',
                repeat: Infinity,
              }}
            />

            {/* Button */}

            <div
              style={{
                background: 'linear-gradient(90deg, #0380F3 0%, #024A8D 100%)',
              }}
              className='text-white text-[10px] z-20   rounded-[4px]  px-2.5  py-[4px] font-semibold  '
            >
              Most Used
            </div>
          </div>
        </div>
      </ShouldRender>
      <ShouldRender check={countryState.selectedCountry?.code === 'IN'}>
        <h3
          className={clsx(
            'text-xs md:text-sm font-normal md:font-medium leading-6 mt-3',
          )}
        >
          Pre-Paid (UPI, Credit card, Debit card)
        </h3>
      </ShouldRender>
      <span className='text-xs flex  gap-x-1.5 items-center text-[#646464]'>
        <span className=''>
          <LockClosedIcon className='w-3.5 h-3.5 -translate-y-[1px] text-grayZl-600' />
        </span>
        All transactions are secure and encrypted
      </span>
      <ShouldRender check={countryState.selectedCountry?.code === 'IN'}>
        <div className='flex h-auto max-w-[300px] mt-3'>
          <Image
            src={CardIcons}
            alt='OnlinePayment'
            className='object-fill w-full '
          />
        </div>
      </ShouldRender>
      <ShouldRender check={countryState.selectedCountry?.code !== 'IN'}>
        <div className='flex  w-full  max-w-[320px] mt-3 pr-20'>
          <Image
            src={InternationalCardIcons}
            alt='OnlinePayment'
            className='object-fill w-auto '
          />
        </div>
      </ShouldRender>

      <ul className='list-disc mt-4 flex flex-col gap-y-2'>
        {offers.map((offer, index) => (
          <ShouldRender key={`${offer?.text}-${index}`} check={offer.check}>
            <li className='flex border-[#FFFFFF0D] border-[1px] rounded-lg w-fit bg-[#FFFFFF0D] list-disc py-1.5 px-2.5 items-center  space-x-1 md:space-x-2'>
              {offer.image}
              <div className='flex gap-x-1 md:gap-x-2 items-center text-[#A7A7A7]'>
                {offer.text}
              </div>
              <ShouldRender check={index === 1}>
                <Tooltip
                  title={`10 Z coin = ${globalCountry?.currency_symbol}
                ${rate ? rate : '...'}`}
                >
                  <span>
                    <InfoCircleIcon width='18' />
                  </span>
                </Tooltip>
              </ShouldRender>
            </li>
          </ShouldRender>
        ))}
      </ul>
    </div>
  );
};

export default PaymentAvailability;
